import {
  type PortalHost,
  getConfiguredPortalHosts,
  getPortalBaseUrl,
  normalizeHost,
} from "@/utils/portalConfig";

const LOCAL_PORTAL_OVERRIDE_KEY = "confidacare_portal_override";

const PORTAL_HOSTS = getConfiguredPortalHosts(import.meta.env);

export function getConfiguredPortalFromHost(hostname: string): PortalHost | null {
  const host = normalizeHost(hostname);
  if (!host) return null;

  if (PORTAL_HOSTS.provider.has(host)) return "provider";
  if (PORTAL_HOSTS.admin.has(host)) return "admin";
  if (PORTAL_HOSTS.patient.has(host)) return "patient";

  const baseDomain = ".confidacare.com";

  if (host.endsWith(baseDomain)) {
    const subdomain = host.slice(0, -baseDomain.length).toLowerCase();

    if (subdomain === "provider" || subdomain.startsWith("provider-")) {
      return "provider";
    }

    if (subdomain === "admin" || subdomain.startsWith("admin-")) {
      return "admin";
    }

    if (
      subdomain === "www" ||
      subdomain === "demo" ||
      subdomain === "staging" ||
      subdomain === "patient" ||
      subdomain.startsWith("patient-")
    ) {
      return "patient";
    }
  }

  return null;
}

function getCurrentLocation() {
  if (typeof window === "undefined") {
    return {
      hostname: "",
      pathname: "/",
      search: "",
    };
  }
  return window.location;
}

export function getPortalHost(): PortalHost {
  const location = getCurrentLocation();
  const hostname = normalizeHost(location.hostname);
  const pathname = location.pathname.toLowerCase();
  const query = new URLSearchParams(location.search);
  const portalQuery = (query.get("portal") ?? "").toLowerCase();

  const configured = getConfiguredPortalFromHost(hostname);
  if (configured) {
    return configured;
  }

  if (portalQuery === "provider") {
    try {
      if (typeof window !== "undefined") window.sessionStorage.setItem(LOCAL_PORTAL_OVERRIDE_KEY, "provider");
    } catch {}
    return "provider";
  }
  if (portalQuery === "admin") {
    try {
      if (typeof window !== "undefined") window.sessionStorage.setItem(LOCAL_PORTAL_OVERRIDE_KEY, "admin");
    } catch {}
    return "admin";
  }

  try {
    const sticky = typeof window !== "undefined"
      ? window.sessionStorage.getItem(LOCAL_PORTAL_OVERRIDE_KEY)
      : null;
    if (sticky === "provider" || sticky === "admin") return sticky;
  } catch {}

  try {
    if (typeof window !== "undefined") {
      const saved = window.localStorage.getItem("confidacare_auth");
      if (saved) {
        const parsed = JSON.parse(saved) as { scope?: string; user?: { role?: string } };
        if (parsed.scope === "providers" || parsed.user?.role === "provider" || parsed.user?.role === "staff") return "provider";
        if (parsed.scope === "admins" || parsed.user?.role === "admin") return "admin";
      }
    }
  } catch {}

  if (pathname.startsWith("/provider/") || pathname.startsWith("/providers/")) return "provider";
  if (pathname === "/provider" || pathname === "/providers") return "provider";
  if (pathname.startsWith("/admin/") || pathname.startsWith("/admins/")) return "admin";
  if (pathname === "/admin" || pathname === "/admins") return "admin";

  return "patient";
}

export function isProviderHost() {
  return getPortalHost() === "provider";
}

export function isAdminHost() {
  return getPortalHost() === "admin";
}

export function isPatientHost() {
  return getPortalHost() === "patient";
}

export function clearPortalHostOverride() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(LOCAL_PORTAL_OVERRIDE_KEY);
  } catch {}
}

export function getPortalLoginHref(target: PortalHost) {
  const providerBaseUrl = getPortalBaseUrl(import.meta.env, "provider");
  const adminBaseUrl = getPortalBaseUrl(import.meta.env, "admin");
  const defaultBaseUrl = getPortalBaseUrl(import.meta.env, "patient");
  const providerFallbackPath = "/providers/login";
  const adminFallbackPath = "/admins/login";
  const patientFallbackPath = "/user/login";

  if (typeof window === "undefined") {
    if (target === "provider") {
      return providerBaseUrl?.trim()
        ? buildPortalUrl(providerBaseUrl, "/login")
        : providerFallbackPath;
    }
    if (target === "admin") {
      return adminBaseUrl?.trim()
        ? buildPortalUrl(adminBaseUrl, "/login")
        : adminFallbackPath;
    }
    return defaultBaseUrl?.trim()
      ? buildPortalUrl(defaultBaseUrl, patientFallbackPath)
      : patientFallbackPath;
  }

  const { hostname } = window.location;
  const host = normalizeHost(hostname);

  if (host === "localhost" || host === "127.0.0.1") {
    // Prefer configured cross-portal URLs even during localhost development.
    if (target === "provider") {
      return providerBaseUrl?.trim()
        ? buildPortalUrl(providerBaseUrl, "/login")
        : providerFallbackPath;
    }
    if (target === "admin") {
      return adminBaseUrl?.trim()
        ? buildPortalUrl(adminBaseUrl, "/login")
        : adminFallbackPath;
    }
    return patientFallbackPath;
  }

  if (target === "provider") {
    return providerBaseUrl?.trim()
      ? buildPortalUrl(providerBaseUrl, "/login")
      : providerFallbackPath;
  }
  if (target === "admin") {
    return adminBaseUrl?.trim()
      ? buildPortalUrl(adminBaseUrl, "/login")
      : adminFallbackPath;
  }
  return defaultBaseUrl?.trim()
    ? buildPortalUrl(defaultBaseUrl, patientFallbackPath)
    : patientFallbackPath;
}

export function buildPortalUrl(baseUrl: string | undefined, loginPath: string) {
  const cleanPath = loginPath.startsWith("/") ? loginPath : `/${loginPath}`;
  if (!baseUrl?.trim()) return cleanPath;

  const raw = baseUrl.trim();
  const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw)
    ? raw
    : `${typeof window !== "undefined" ? window.location.protocol : "https:"}//${raw}`;

  try {
    const origin = new URL(withProtocol).origin;
    return `${origin}${cleanPath}`;
  } catch {
    return cleanPath;
  }
}
